import { CrudDecorator, DataSourceExecutionEvent, DataSourceMode, Label, Layout, Panel, TableRow, TableRowDisplayEvent, TableSelectionEvent } from "@mcleod/components";
import { DisplayType, DisplayValue, LogManager, ModelRow, Navigation } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { FilterPanel, SecondaryFilterProps } from "../planning/FilterPanel";
import { AutogenLayoutMobileCommFormGroupByVendor } from "./autogen/AutogenLayoutMobileCommFormGroupByVendor";
import { FormFieldType } from "./FormFieldRenderer";
import { MobileCommForm } from "./MobileCommForm";
import { MobileCommExAppControl } from "./MobileCommExAppControl";

const log = LogManager.getLogger("lme.dispatch.mobilecomm.MobileCommFormGroupByTable");

export class MobileCommFormGroupByVendor extends AutogenLayoutMobileCommFormGroupByVendor {
    public filterPanel: FilterPanel;

    ACTIVE_FORM: SecondaryFilterProps = {
        id: "activeForm",
        caption: "Active",
        filter: (row: ModelRow) => {
            return row.get("is_active") == "Y";
        }
    }
    INACTIVE_FORM: SecondaryFilterProps = {
        id: "inactiveForm",
        caption: "Inactive",
        filter: (row: ModelRow) => {
            return row.get("is_active") != "Y";
        }
    }
    INBOUND_FORM: SecondaryFilterProps = {
        id: "inboundForm",
        caption: "Inbound",
        filter: (row: ModelRow) => {
            return row.get("form_type") == "I";
        }
    }
    OUTBOUND_FORM: SecondaryFilterProps = {
        id: "outboundForm",
        caption: "Outbound",
        filter: (row: ModelRow) => {
            return row.get("form_type") == "O";
        }
    }

    onLoad(): void | Promise<void> {
        this.addLayoutLoadListener(async (layout) => {
            // Add the filter panel to the custom tools panel
            const rightToolsPanel = this.tableFormByVendor.headingPanel.findComponentById("tableFormByVendorHeadingPanel-panelCustomRightTools") as Panel;
            if (rightToolsPanel) {
                const buttons = [...(rightToolsPanel.components || [])];
                this.filterPanel = new FilterPanel([this.ACTIVE_FORM, this.INACTIVE_FORM, this.INBOUND_FORM, this.OUTBOUND_FORM], this.tableFormByVendor, rightToolsPanel);
                rightToolsPanel.add(...buttons);
            }
        });

        this.tableFormByVendor.overrideUrlParams = (selectedRow: TableRow) => {
            return {
                key: selectedRow.data.get("id"),
                formType: selectedRow.data.get("form_type", selectedRow.data.get("form_type")),
                mcControlId: selectedRow.data.get("mc_control_id", selectedRow.data.get("mc_control_id")),
            };
        }
        this.tableFormByVendor.addSelectionListener((event: TableSelectionEvent) => this.onSelectionChanged(event));
        this.tableFormByVendor.doOnDataSourceChanged = (ds, value) => {
            this.updateTables();
        }
    }

    private async updateTables(formId?: any) {
        if (this.owner.layoutFormGroupByTable) {
            const selectedIndex = this.owner.layoutFormGroupByTable.tableFormGroupBy.selectedIndex;
            await this.owner.layoutFormGroupByTable.tableFormGroupBy.dataSource.search(this.owner.layoutFormGroupByTable.tableFormGroupBy.dataSource.lastSearch);
            this.owner.layoutFormGroupByVendor.mainDataSource.addAfterExecutionListener(async (event: DataSourceExecutionEvent) => {
                if ("search" == event.getAction()) {
                    await this.selectRowByFormId(formId);
                    await this.filterPanel.updateFilterCounts();
                }
            });

            this.owner.layoutFormGroupByTable.tableFormGroupBy.selectedIndex = selectedIndex;
        } else {
            await this.mainDataSource.search(this.mainDataSource.lastSearch);
        }
    }

    private onSelectionChanged(event: TableSelectionEvent) {
        this.buttonCopy.enabled = this.buttonUpload.enabled = this.tableFormByVendor.selectedRows.length == 1;
        this.buttonExApps.enabled = this.tableFormByVendor.selectedRows.length == 1 && this.tableFormByVendor.selectedRow.data.get("form_type") == "I";
    }

    async tableAfterCrudClose(event) {
        if (event.dataSourceMode === DataSourceMode.ADD || event.dataSourceMode === DataSourceMode.UPDATE) {
            if (this?.filterPanel) {
                this.filterPanel.updateFilterCounts();
            }
        }
    }

    tableFormGroupByVendorOnRowDisplay(event: TableRowDisplayEvent): void {
        const tableRow = event.getTableRow();

        this.updateIsActiveLabel(tableRow);
        this.setupTooltips(tableRow);
    }

    /**
     * Updates the active status label with appropriate icon based on the is_active field.
     */
    private updateIsActiveLabel(tableRow: any): void {
        const labelIsActive = tableRow.findComponentById("labelIsActive") as Label;
        if (!labelIsActive) {
            return;
        }

        const isActive = tableRow.data?.getBoolean("is_active", null);
        labelIsActive.text = "";
        labelIsActive.imageName = isActive ? ImageName.circleCheck : ImageName.circleCheckEmpty;
    }

    /**
     * Sets up tooltip callbacks for both title and form number components.
     */
    private setupTooltips(tableRow: any): void {
        const vendorId = tableRow.data?.get("mc_control_id", null);
        const formId = tableRow.data?.get("id", null);
        const formType = tableRow.data?.get("form_type", null);

        this.setupTooltipForComponent(tableRow, "textboxTitle", vendorId, formId, formType);
        this.setupTooltipForComponent(tableRow, "textboxFormNumber", vendorId, formId, formType);
    }

    /**
     * Sets up tooltip callback for a specific component.
     */
    private setupTooltipForComponent(
        tableRow: any,
        componentId: string,
        vendorId: any,
        formId: any,
        formType: any
    ): void {
        const component = tableRow.findComponentById(componentId) as Label;
        if (!component) {
            return;
        }

        component.tooltipDynamicContent = async () => {
            const layout = await Layout.getLoadedLayout("lme/dispatch/mobilecomm/MobileCommFormQuickInfo") as Layout;
            await layout.mainDataSource?.search({
                id: formId,
                mc_control_id: vendorId,
                form_type: formType
            });
            return layout;
        };
    }

    buttonCopyOnClick() {
        const formLayout = Layout.getLayout("lme/dispatch/mobilecomm/MobileCommForm") as MobileCommForm;
        const formPanel = new CrudDecorator({
            layout: formLayout,
            mode: DataSourceMode.ADD,
            headerProps: {
                showClose: true,
                showSaveAndClose: true
            },
            doAfterSlideIn: async (layout) => {
                formLayout.addLayoutLoadListener(async (layout) => {
                    const selectedRow = this.tableFormByVendor.selectedRow;

                    await formLayout.sourceMobileCommForm?.search({
                        id: selectedRow.data.get("id"),
                        mc_control_id: selectedRow.data.get("mc_control_id"),
                        form_type: selectedRow.data.get("form_type")
                    });

                    await formLayout.sourceMobileCommFormFieldOpt?.search({
                        form_id: selectedRow.data.get("id"),
                        mc_control_id: selectedRow.data.get("mc_control_id"),
                    });

                    await formLayout.sourceMobileCommForm.setRowsAndMode(DataSourceMode.ADD, [formLayout.sourceMobileCommForm.activeRow]);
                    formLayout.sourceMobileCommForm.activeRow.set("mc_control_id", null);
                    formLayout.sourceMobileCommForm.activeRow.set("creation_date", new Date());
                    formLayout.textboxVendor.focus();
                    formLayout.sourceMobileCommForm.activeRow._appending = true

                    await formLayout.sourceMobileCommForm.activeRow.addLinkedModel({
                        model: formLayout.sourceMobileCommFormFieldOpt.url,
                        rows: formLayout.sourceMobileCommFormFieldOpt.data
                    });

                    formLayout.sourceMobileCommForm.activeRow.addBeforePostListener(async postEvent => {
                        const mcControlId = formLayout.sourceMobileCommForm.activeRow.get("mc_control_id");
                        const formId = formLayout.sourceMobileCommForm.activeRow.get("id");

                        let fieldNumberCounter = 0;
                        for (const row of formLayout.tableFormFieldOpt.allRows) {
                            row.data._appending = true;
                            this.updateSingleRowData(row, mcControlId, formId, fieldNumberCounter);
                            row.data.set("id", null);
                            if (row.data.get("field_type") !== FormFieldType.CAPTION) {
                                fieldNumberCounter++;
                            }
                        }
                    });


                    formLayout.buttonCopy.visible = false;
                });
            },
            doAfterSlideOut: async () => {
                const formId = formLayout.mainDataSource?.activeRow.get("id");
                const formType = formLayout.mainDataSource?.activeRow.get("form_type");
                const vendor = formLayout.mainDataSource?.activeRow.get("mc_control_id");
                // I would rather determine if the slideout was committed vs closed, and only update if committed
                // But doing it this way, for now, will have to suffice
                if (formId != null && formType != null && vendor != null) {
                    await this.updateTables(formId);
                }
            }
        });

        formPanel.slideIn({ speed: 200 });
    }

    async buttonNewFormOnClick() {
        let formVendor, vendorName;

        if (this.owner.layoutFormGroupByTable) {
            const selectedRow = this.owner.layoutFormGroupByTable.tableFormGroupBy.selectedRow;
            formVendor = selectedRow?.data.get("form_vendor");
            vendorName = selectedRow?.data.get("vendor_name");
        } else {
            formVendor = this.owner.sourceMCControl?.activeRow.get("id");
            vendorName = this.owner.sourceMCControl?.activeRow.get("vendor_name");
        }

        const formLayout = await Layout.getLayout("lme/dispatch/mobilecomm/MobileCommForm") as MobileCommForm;
        const formPanel = new CrudDecorator({
            layout: formLayout,
            mode: DataSourceMode.ADD,
            headerProps: {
                showClose: true,
                showSaveAndClose: true
            },
            doAfterSlideIn: async () => {
                formLayout.addLayoutLoadListener(async (layout) => {
                    const saveButton = formLayout.dataHeader?.["saveButton"];
                    if (saveButton) {
                        saveButton.allowSaveAndAdd = true;
                    }

                    await formLayout.mainDataSource.setRowsAndMode(DataSourceMode.ADD, [await formLayout.mainDataSource.createBlankRow()]);
                    await formLayout.activeRow.set({
                        is_active: "Y",
                        status: "N",
                        mc_control_id: formVendor,
                        _lookup_mc_control_id: { id: formVendor, vendor_name: vendorName }
                    });
                    formLayout.buttonCopy.visible = false;
                    formLayout.sourceMobileCommForm.displayDataInBoundComponents();
                    formLayout.textboxCreationDate.text = DisplayValue.getDateDisplayValue(new Date(), DisplayType.DATETIME);
                    formLayout.textboxId.focus();
                });
            },
            doAfterSlideOut: async () => {
                const formId = formLayout.mainDataSource?.activeRow.get("id");
                const formType = formLayout.mainDataSource?.activeRow.get("form_type");
                const vendor = formLayout.mainDataSource?.activeRow.get("mc_control_id");
                if (formId != null && formType != null && vendor != null) {
                    await this.updateTables(formId);
                }
            }
        });

        formPanel.slideIn({ speed: 200 });
    }

    /**
     * Finds and selects a row in the table by form ID.
     * @param formId The form ID to search for
     */
    async selectRowByFormId(formId: any): Promise<void> {
        if (!this.tableFormByVendor || !formId) {
            return;
        }

        for (let i = 0; i < this.tableFormByVendor.rows.length; i++) {
            const row = this.tableFormByVendor.rows[i];
            if (row.data.get("id") === formId) {
                this.tableFormByVendor.selectedIndex = i;
                this.tableFormByVendor.selectedRow?.scrollIntoView();
                return;
            }
        }
    }

    private updateSingleRowData(row: TableRow, mcControlId: any, formId: any, fieldNumberCounter: number): void {
        const rowData = row.data;

        // Set basic form identifiers
        rowData.set("mc_control_id", mcControlId);
        rowData.set("form_id", formId);

        // Set forced field data
        rowData.set("forced_x_placement", this.parseNumberValue(rowData.get("x_placement")));
        rowData.set("forced_y_placement", this.parseNumberValue(rowData.get("y_placement")));
        rowData.set("forced_row_break", rowData.get("row_break"));
        rowData.set("forced_field_type", rowData.get("field_type"));
        rowData.set("forced_field_text", rowData.get("text"));
        rowData.set("forced_field_length", this.parseNumberValue(rowData.get("length")));
        rowData.set("forced_field_value", rowData.get("field_value"));
        rowData.set("forced_reference_qual", rowData.get("reference_qual"));
        rowData.set("forced_field_number", fieldNumberCounter);
    }

    private parseNumberValue(value: any): number {
        if (value === null || value === undefined || value === '') {
            return 0;
        }

        const parsed = parseInt(value);
        return isNaN(parsed) ? 0 : parsed;
    }

    /** This is an event handler for the onClick event of buttonExApps. */
    buttonExAppsOnClick() {
        const id = this.tableFormByVendor.selectedRow.data.get("id");
        const mcControlId = this.mainDataSource?.activeRow?.get("mc_control_id");
        Navigation.navigateTo(`lme/dispatch/mobilecomm/MobileCommExAppControl?id=${id}&mc_control_id=${mcControlId}`, {newTab: true});
    }
}
